import PropTypes from "prop-types";

import styled from "styled-components/macro";

import CopyToClipboardSelector from "Signups/FamilyRegistration/Shared/CopyToClipboard";
import { AnimatePresence, motion } from "framer-motion";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import FnrIcon from "UI/FnrIcon";
import { colors } from "UI/colors";
import { wrapLongWordsNoHyphen } from "UI/styles";

const FnrPreviewBar = (
  /**
   * @param {string} url - the url to copy to clipboard and display
   * @param {string} previewUrl - the url to open from preview button (overrides url if present)
   * @param {string} title
   * @param {boolean} shouldHideInitial
   * @param {boolean} shouldHideAnimation
   * @param {boolean} showActionButtons - if true, will render action buttons
   * @param {function} renderActionButtons - a function that returns a component to render in the action buttons section
   * @param {boolean} isPublic - if false, will render a lock icon
   * @param {boolean} composerMinimized - if true, will render a spacer to push the composer up
   * @param {boolean} overridePositioningStyles - if true, will not use absolute positioning
   * @param {function} titleButtons - a function that returns component to render additional buttons in the title section
   *
   */
  {
    url,
    previewUrl,
    title,
    shouldHideInitial,
    shouldHideAnimation,
    showActionButtons,
    renderActionButtons,
    isPublic,
    composerMinimized,
    overridePositioningStyles,
    titleButtons,
    ...rest
  }
) => {
  return (
    <Wrapper
      data-cy="FnrPreviewBar_Container"
      $overridePositioningStyles={overridePositioningStyles}
      initial={
        shouldHideInitial
          ? { opacity: 0, y: "100%", height: 0 }
          : { opacity: 1, y: 0, height: "auto" }
      }
      animate={
        shouldHideAnimation
          ? { opacity: 0, y: "100%", height: 0 }
          : { opacity: 1, y: 0, height: "auto" }
      }
      {...rest}
    >
      <AnimatePresence>
        {!shouldHideInitial && !shouldHideAnimation && (
          <Inside
            initial={{ height: "auto" }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
          >
            <Text>
              <UrlTitle>
                {title}
                {!isPublic && (
                  <FnrIcon fas fa-lock-alt style={{ fontSize: 10 }} />
                )}
              </UrlTitle>
              <A href={url} target="_blank" rel="noreferrer">
                {url}
              </A>{" "}
              <CopyToClipboardSelector text={url} />
              {titleButtons && titleButtons()}
            </Text>
            <Buttons>
              <PreviewButton
                data-cy="FnrPreviewBar_PreviewButton"
                href={previewUrl ? previewUrl : url}
                target="_blank"
                rel="noreferrer"
              >
                <FnrButton>
                  <ButtonIcon fas fa-external-link />
                  <ButtonText>Preview</ButtonText>
                </FnrButton>
              </PreviewButton>

              {showActionButtons &&
                renderActionButtons &&
                renderActionButtons()}
            </Buttons>
          </Inside>
        )}
      </AnimatePresence>
      {composerMinimized && (
        <ComposerSpacer
          data-cy="composerSpacer"
          animate={{ height: composerMinimized ? 90 : 0 }}
        />
      )}
    </Wrapper>
  );
};

FnrPreviewBar.propTypes = {
  url: PropTypes.string.isRequired,
  previewUrl: PropTypes.string,
  title: PropTypes.string.isRequired,
  shouldHideInitial: PropTypes.bool,
  shouldHideAnimation: PropTypes.bool,
  showActionButtons: PropTypes.bool,
  renderActionButtons: PropTypes.func,
  composerMinimized: PropTypes.bool,
  isPublic: PropTypes.bool,
  overridePositioningStyles: PropTypes.bool,
  titleButtons: PropTypes.func,
};

export default FnrPreviewBar;

const Wrapper = styled(motion.div)`
  ${(props) =>
    props.$overridePositioningStyles
      ? ""
      : `
    flex-shrink: 0;
    box-sizing: border-box;
    position: absolute;
    z-index: 1000;
    bottom: 0;
    left: 12px;
    right: 52px;
  `}
`;
const Inside = styled(motion.div)`
  border-radius: 18px;
  padding: 12px;
  padding-left: 18px;
  background: #fff;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  box-shadow: rgba(0, 0, 0, 0.1) 0 1px 4px;
  border: 1px solid ${colors.fnGray200};
  background: linear-gradient(
    to right bottom,
    rgba(19, 66, 107, 0.8),
    rgba(47, 63, 79, 0.8)
  );
  backdrop-filter: blur(12px) saturate(2);
  -webkit-backdrop-filter: blur(12px) saturate(2);
  color: #eceff0;
  margin-bottom: 12px;

  @media (max-width: 1025px) {
    flex-direction: column;
    gap: 2px;
    padding: 6px 4px 4px;
  }
`;
const Text = styled.div`
  max-width: 100%;
  ${wrapLongWordsNoHyphen}
`;
const Buttons = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  a {
    display: block;
  }
`;
const A = styled.a`
  color: inherit;
  text-decoration: none;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-wrap: break-word;

  &:hover {
    text-decoration: underline;
  }
`;
const UrlTitle = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${colors.fnGray200};
  text-transform: uppercase;
  display: flex;
  gap: 6px;
  align-items: center;

  @media (max-width: 1025px) {
    display: none;
  }
`;

const ComposerSpacer = styled(motion.div)``;

const PreviewButton = styled.a`
  text-decoration: none;

  &:hover {
    text-decoration: none !important;
  }

  @media (max-width: 1180px) {
    display: none !important;
  }
`;
